import { SPINNER_DATA_URI } from './assets'

/**
 * The whole look, scoped to the shadow root. It is a straight port of the
 * original scoped Vue component, with `.page-loader` folded into `:host` (the
 * element itself is the full-screen overlay) and an opacity transition added so
 * `hide()` can fade the screen out before it detaches.
 *
 * Colours are the loader's own, on purpose: it must look identical wherever it
 * is dropped, regardless of the host app's theme tokens.
 */
export const STYLES = `
:host {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  opacity: 1;
  transition: opacity 0.35s ease;

  /**
   *  Typography is declared here, never inherited — and with !important.
   *
   *  The shadow root keeps the host page's SELECTORS out, but inherited
   *  properties still cross the boundary: these screens open games and
   *  dashboards that each set their own font, size, weight and even
   *  text-transform on <body>.
   *
   *  Declaring them was not enough. When a shadow-tree rule and an outer-page
   *  rule both target the host, the cascade hands NORMAL declarations to the
   *  outer page — so a game with a universal selector setting text-transform
   *  (a universal selector does match the host element) still won, and only in
   *  those games, which is why it looked intermittent. For IMPORTANT
   *  declarations that same rule flips and the shadow tree wins. Hence the
   *  !important: it is the only way to make the splash identical everywhere.
   */
  font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, 'Helvetica Neue', Arial,
    sans-serif !important;
  font-size: 16px !important;
  font-style: normal !important;
  font-weight: 400 !important;
  font-variant: normal !important;
  line-height: 1.4 !important;
  letter-spacing: normal !important;
  word-spacing: normal !important;
  text-transform: none !important;
  text-align: center !important;
  text-indent: 0 !important;
  text-shadow: none !important;
  white-space: normal !important;
  direction: ltr !important;
  color: #ffffff !important;

  --color-bg-banana-loader: #0b0b0d;
  --bar-secondary-color: #4a3406;
  --bar-primary-color: #ffad00;
  --bar-border-radius: 999px;
  --bar-height: 14px;
  --bar-width: 280px;
  --font-muted-color: #a4a4a4;
  --font-size-small-text: 14px;
  --spinner-svg: url('${SPINNER_DATA_URI}');

  background:
    radial-gradient(circle at 50% 38%, #1d1608 0%, transparent 62%),
    var(--color-bg-banana-loader);
}

:host([hidden]) { display: none; }

:host(.is-hiding) {
  opacity: 0;
  pointer-events: none;
}

@media (min-width: 420px) {
  :host { --bar-width: 370px; }
}

*, *::before, *::after { box-sizing: border-box; }

/** Wraps the mark so the glow can be centred on the LOGO, not on a fixed
 *  fraction of the viewport — it tracks the logo at any size or layout. */
.page-loader__mark-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-loader__glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 460px;
  height: 460px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: radial-gradient(circle, #ffad0033 0%, transparent 68%);
  filter: blur(6px);
  animation: glow-breathe 3.4s ease-in-out infinite;
  pointer-events: none;
  z-index: 0;
}

.page-loader__sparks {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.spark {
  position: absolute;
  bottom: -10px;
  border-radius: 50%;
  background: var(--bar-primary-color);
  opacity: 0;
  box-shadow: 0 0 6px var(--bar-primary-color);
  animation-name: spark-rise;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}

.page-loader__inner {
  position: relative;
  z-index: 2;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 46px;
}

.page-loader__mark {
  max-width: 192px;
  z-index: 20;
  filter: drop-shadow(0 0 26px #ffad0055);
  animation: mark-in 0.7s cubic-bezier(0.2, 1.2, 0.3, 1) both;
}

.page-loader__mark svg { display: block; width: 100%; height: auto; }

.page-loader__box {
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.page-loader__text {
  font-size: var(--font-size-small-text);
  color: var(--bar-primary-color);
  font-weight: 500;
  letter-spacing: 0.04em;
  padding-top: 16px;
  z-index: 3;
  margin: 0;
  text-align: center;
}

.page-loader__text.is-done {
  animation: done-pop 0.4s ease-out;
}

.page-loader__text-muted {
  color: var(--font-muted-color);
  margin-left: 0.4em;
}

.page-loader__bar-wrapper {
  position: relative;
  height: var(--bar-height);
  width: var(--bar-width);
}

/**
 *  The spinner rides the bar.
 *
 *  Painted as a background rather than a content: url(...) image: a replaced
 *  element that also spins has to be re-rastered as it turns, and these screens
 *  open while a game is busy loading WebGL and decoding assets. Starved of the
 *  main thread, the spinner would stall at the start and then jump to catch up
 *  while the bar — a plain gradient — sailed on without it.
 *
 *  will-change gets it its own compositor layer up front, so the spin is the
 *  compositor's job and boot work can't hold it back.
 */
.page-loader__bar-wrapper::after {
  --slide-spinner-translate: var(--bar-width);
  /**
   *  The spinner's own size, in px so it is the same on every surface — it must
   *  not scale with the bar, the viewport or anything the host page does.
   *
   *  The offsets place its CENTRE on the bar's left edge, vertically centred on
   *  the bar's 14px height: half the spinner to the left (-27px), and 7px minus
   *  half the spinner above (-20px). It then travels one bar-width, so its
   *  centre tracks the leading edge of the fill the whole way.
   */
  --spinner-size: 54px;

  content: '';
  position: absolute;
  top: -20px;
  left: -27px;
  width: var(--spinner-size);
  height: var(--spinner-size);
  background-image: var(--spinner-svg);
  background-repeat: no-repeat;
  background-position: center;
  background-size: var(--spinner-size) var(--spinner-size);
  will-change: transform;
  backface-visibility: hidden;
  /**
   *  El eje de giro va apenas por encima del centro, así que en vez de girar
   *  clavado describe una órbita chica y sube y baja mientras avanza.
   *
   *  No es un giro imperfecto por descuido: es lo que le da el toque simpático.
   *  Antes salía solo, pero por accidente — la imagen quedaba descentrada por
   *  las métricas de línea que heredaba de la página, así que el efecto
   *  cambiaba de un juego a otro. Declarado acá, es igual en todos lados.
   */
  transform-origin: 50% 45%;
  animation: slideSpinner var(--timed-title-time-left, 3000ms) linear forwards;
  z-index: 7;
}

.page-loader__bar {
  position: relative;
  height: var(--bar-height);
  width: var(--bar-width);
  z-index: 3;
  background-color: var(--bar-secondary-color);
  border-radius: var(--bar-border-radius);
  overflow: hidden;
  box-shadow:
    inset 0 1px 2px #00000080,
    0 0 22px #ffb00566;
}

.page-loader__bar::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 3;
  background: linear-gradient(90deg, #b7870a 0%, var(--bar-primary-color) 60%, #ffd76b 100%);
  will-change: transform;
  animation: slideLoader var(--timed-title-time-left, 3000ms) linear forwards;
}

.page-loader__bar::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 4;
  background: linear-gradient(
    100deg,
    transparent 20%,
    #ffffff66 45%,
    #ffffffaa 50%,
    #ffffff66 55%,
    transparent 80%
  );
  transform: translateX(-100%);
  animation: shimmer 1.4s ease-in-out infinite;
}

@keyframes slideLoader {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(0); }
}

/** El transform-origin lo fija la regla del spinner (levemente descentrado, para
 *  el sube-y-baja): declararlo acá lo pisaría, porque las animaciones ganan. */
@keyframes slideSpinner {
  0% {
    transform: translateX(0) rotate(0deg);
  }
  100% {
    transform: translateX(var(--slide-spinner-translate)) rotate(2600deg);
  }
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  60%, 100% { transform: translateX(100%); }
}

@keyframes glow-breathe {
  0%, 100% {
    opacity: 0.55;
    transform: translate(-50%, -50%) scale(0.94);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.06);
  }
}

@keyframes spark-rise {
  0% {
    opacity: 0;
    transform: translateY(0) scale(0.6);
  }
  15% { opacity: 0.9; }
  85% { opacity: 0.5; }
  100% {
    opacity: 0;
    transform: translateY(-100vh) scale(1);
  }
}

@keyframes mark-in {
  from {
    opacity: 0;
    transform: scale(0.86) translateY(10px);
  }
}

@keyframes done-pop {
  from {
    transform: scale(0.9);
    opacity: 0.4;
  }
}

@media (prefers-reduced-motion: reduce) {
  :host { transition: none; }
  .page-loader__glow,
  .page-loader__mark,
  .page-loader__bar::after,
  .page-loader__text.is-done {
    animation: none;
  }
  .spark { display: none; }
}

.banana {
  transition: fill-opacity 0.5s ease-in-out;
}
`
